import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DrawerLink } from '../../app/DrawerLink';
import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { Select } from '../../components/ui/form';
import { LoadingPanel } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { copy } from '../../config/copy';
import { useFormErrors } from '../auth/useFormErrors';
import { useCan, useSession } from '../../stores/session';
import { Checkbox, Textarea, TextInput } from '../../components/ui/form';

/**
 * Points per earned boost.
 *
 * Mirrored from `DiscoveryCredits::THRESHOLD` rather than fetched: this screen
 * only uses it to label the shortcut buttons, and a round trip so an admin
 * panel can print "100" would be silly. The server is still the authority on
 * what a grant actually produces, and says so in its response.
 */
const THRESHOLD = 100;

interface AdminUserDetail {
  user: {
    id: number;
    username: string;
    display_name: string;
    verified?: boolean;
    email: string;
    status: string;
    email_verified: boolean;
    email_status: string;
    created_at: string;
    last_seen_at: string | null;
    avatar: string | null;
    banner: string | null;
    bio: string | null;
    location: string | null;
    follower_count: number;
    following_count: number;
  };
  roles: string[];
  tracks: {
    id: number;
    title: string;
    status: string;
    visibility: string;
    held: boolean;
    artwork: string | null;
    created_at: string;
  }[];
  /** The id to ask from for the next page of uploads, or null at the end. */
  tracks_cursor: string | null;
  available_roles: { name: string; label: string; description: string | null; is_system: boolean }[];
  /*
   * Named rather than an open record. `Record<string, number>` makes every
   * lookup `number | undefined` under `noUncheckedIndexedAccess`, and the six
   * keys are fixed by the one query in `ShowUserAction::counts()`.
   */
  /** The mute in force, and every one before it. */
  mute: { reason: string; expires_at: string | null; created_at: string; by: string | null } | null;
  mutes: {
    id: number;
    reason: string;
    expires_at: string | null;
    lifted_at: string | null;
    created_at: string;
    muted_by: string | null;
    lifted_by: string | null;
  }[];
  counts: {
    tracks: number;
    tracks_live: number;
    tracks_held: number;
    comments: number;
    collections: number;
    plays: number;
  };
  host_applications: {
    id: number;
    status: string;
    country: string;
    created_at: string;
    decided_at: string | null;
    decision_note: string | null;
    reviewer: string | null;
  }[];
  /*
   * All three are absent rather than empty when the viewer lacks the
   * permission, so the page can leave the section out entirely. "No notes" and
   * "you may not read notes" are different facts and a blank panel shows both
   * the same way.
   */
  notes?: {
    id: number;
    body: string | null;
    /** Null once the author's account is gone - see the user_notes migration. */
    author: string | null;
    created_at: string;
    deleted_at: string | null;
    deleted_by: string | null;
  }[];
  reports?: {
    about_them: number;
    about_their_content: number;
    recent: {
      id: number;
      reason: string;
      status: string;
      created_at: string;
      subject_type: string;
      subject_id: number;
    }[];
  };
  history?: {
    id: number;
    action: string;
    created_at: string;
    subject_type: string;
    subject_id: number | null;
    actor: string | null;
  }[];
}

/**
 * One account, everything about it, every action on it.
 *
 * ## Why this exists
 *
 * The users list had two buttons: suspend, and a hardcoded "Make mod". That
 * shape has one bug per role - there was no way to revoke hosting from the
 * admin panel at all, because nobody had added a button for it - and no way to
 * see *why* a decision had been made before making the next one.
 *
 * A list is for finding somebody. This is for deciding about them, and it holds
 * the five things that decision turns on: what they have posted, what has been
 * reported, what roles they hold, what staff have said before, and what has
 * already been done to them.
 *
 * The roles here are enumerated from the server's own table, so a role added
 * next month appears without anybody touching this file.
 */
export function AdminUserPage() {
  const { id } = useParams();
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();

  const canAssignRoles = useCan('user.role.assign');
  const canSuspend = useCan('user.suspend');
  const canVerify = useCan('user.verify');
  const canNote = useCan('user.note');
  const canAward = useCan('discovery.award');
  const canEdit = useCan('user.edit');
  const canMute = useCan('user.mute');
  const canDelete = useCan('user.delete');

  const key = ['admin', 'user', id];

  const { data, isPending, error } = useQuery({
    queryKey: key,
    queryFn: () => api.get<AdminUserDetail>(`/admin/users/${id}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    // The list's badges are derived from the same facts.
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  /*
   * Vouching for the account, and stopping.
   *
   * Separate from suspending, though they sit next to each other. Suspending is
   * moderation - a response to what somebody did. This is a claim the platform
   * makes about who they are, and the two are not the same trust: `user.verify`
   * is its own permission.
   */
  const verify = useMutation({
    mutationFn: (on: boolean) =>
      on ? api.post(`/admin/users/${id}/verify`) : api.delete(`/admin/users/${id}/verify`),
    onSuccess: invalidate,
  });

  const suspend = useMutation({
    mutationFn: (on: boolean) =>
      on ? api.post(`/admin/users/${id}/suspend`) : api.delete(`/admin/users/${id}/suspend`),
    onSuccess: invalidate,
  });

  const role = useMutation({
    mutationFn: ({ name, grant }: { name: string; grant: boolean }) =>
      grant
        ? api.post(`/admin/users/${id}/roles`, { role: name })
        : api.delete(`/admin/users/${id}/roles`, { body: { role: name } }),
    onSuccess: invalidate,
  });

  if (isPending) {
    return (
      <Frame>
        <LoadingPanel />
      </Frame>
    );
  }

  if (error || !data) {
    return (
      <Frame>
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'Could not load that account.'}
        </Alert>
      </Frame>
    );
  }

  const { user } = data;
  const isSelf = user.id === me?.id;
  /*
   * The server's message, verbatim, for both mutations.
   *
   * These are the deliberate refusals - "that is the only active
   * administrator" - and rewording them here would mean maintaining the same
   * reasoning in two places and getting it wrong in one.
   */
  const failure = (suspend.error ?? role.error) as ApiError | null;

  return (
    <Frame>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar src={user.avatar} name={user.display_name} size={56} />
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight text-ink">{user.display_name}</h1>
              {isSelf && <Chip>you</Chip>}
            </div>
            <Link to={`/${user.username}`} className="font-mono text-sm text-faint hover:text-soft">
              @{user.username}
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {user.status !== 'active' && <Chip tone="hot">{user.status}</Chip>}
          {data.mute && (
            <span data-muted-chip>
              <Chip tone="hot">muted</Chip>
            </span>
          )}
          {!user.email_verified && (
            <span data-unverified>
              <Chip tone="hot">unverified</Chip>
            </span>
          )}
          {/* A bounced or complained address is why their mail stops arriving,
              and it is the first thing to check when somebody says they never
              got the link. */}
          {(user.email_status === 'invalid' || user.email_status === 'complained') && (
            <Chip tone="hot">email {user.email_status}</Chip>
          )}
          {data.roles.map((name) => (
            <Chip key={name} tone="signal">
              {name}
            </Chip>
          ))}
        </div>
      </div>

      {failure && <Alert tone="error">{failure.message}</Alert>}

      {/* ------------------------------------------------------------------ */}
      {/* Account                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Card className="grid gap-4 p-5 sm:grid-cols-2">
        <Detail label="Email" value={user.email} mono />
        <Detail label="Joined" value={fullDate(user.created_at)} />
        <Detail label="Last seen" value={user.last_seen_at ? fullDate(user.last_seen_at) : 'Never'} />
        <Detail label="Location" value={user.location ?? '—'} />
        <Detail label="Followers" value={user.follower_count.toLocaleString()} />
        <Detail label="Following" value={user.following_count.toLocaleString()} />
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Tracks" value={data.counts.tracks} />
        <Stat label="Live" value={data.counts.tracks_live} />
        <Stat label="On hold" value={data.counts.tracks_held} tone={data.counts.tracks_held > 0} />
        <Stat label="Albums" value={data.counts.collections} />
        <Stat label="Comments" value={data.counts.comments} />
        <Stat label="Plays" value={data.counts.plays} />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Editing the account                                                 */}
      {/* ------------------------------------------------------------------ */}
      {canEdit && <AccountForm user={user} onChange={invalidate} />}

      {(canEdit || canDelete) && (
        <AccessSection user={user} isSelf={isSelf} canEdit={canEdit} canDelete={canDelete} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Roles                                                               */}
      {/* ------------------------------------------------------------------ */}
      {canAssignRoles && (
        <Section title="Roles">
          <ul className="flex flex-col gap-2" data-roles>
            {data.available_roles.map((available) => {
              const held = data.roles.includes(available.name);

              return (
                <li
                  key={available.name}
                  data-role={available.name}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-control)] border border-line p-3"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-ink">{available.label}</span>
                    {available.description && (
                      <span className="text-xs text-faint">{available.description}</span>
                    )}
                  </div>

                  <Button
                    variant={held ? 'ghost' : 'secondary'}
                    loading={role.isPending && role.variables?.name === available.name}
                    onClick={() => role.mutate({ name: available.name, grant: !held })}
                    data-toggle-role={available.name}
                  >
                    {held ? 'Revoke' : 'Grant'}
                  </Button>
                </li>
              );
            })}
          </ul>

          {/* Said here because it is the one role change the person on the other
              end finds out about, and a moderator should know that before
              pressing it. */}
          {data.roles.includes('host') && (
            <p className="mt-3 text-xs text-faint">
              Revoking <span className="font-mono">host</span> notifies them. Their application stays
              on record, so it can be granted back without a new one.
            </p>
          )}
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Muting                                                              */}
      {/* ------------------------------------------------------------------ */}
      {canMute && <MuteSection user={user} mute={data.mute} history={data.mutes} isSelf={isSelf} onChange={invalidate} />}

      {/* ------------------------------------------------------------------ */}
      {/* Suspension                                                          */}
      {/* ------------------------------------------------------------------ */}
      {canSuspend && (
        <Section title="Account status">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant={user.status === 'active' ? 'danger' : 'secondary'}
              // Disabled rather than hidden: the server refuses this too, and a
              // button that vanishes is more confusing than one visibly
              // unavailable.
              disabled={isSelf}
              loading={suspend.isPending}
              onClick={() => suspend.mutate(user.status === 'active')}
              data-suspend
            >
              {user.status === 'active' ? 'Suspend account' : 'Reinstate account'}
            </Button>
            <p className="text-xs text-faint">
              {isSelf
                ? 'You cannot suspend your own account.'
                : user.status === 'active'
                  ? 'Signs them out, hides their tracks, and stops them signing back in.'
                  : 'Restores their access and puts their tracks back.'}
            </p>
          </div>
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Verification                                                        */}
      {/* ------------------------------------------------------------------ */}
      {canVerify && (
        <Section title="Verification">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant={user.verified ? 'secondary' : 'primary'}
              loading={verify.isPending}
              onClick={() => verify.mutate(!user.verified)}
              data-verify
            >
              {user.verified ? 'Remove the check' : 'Verify this artist'}
            </Button>

            {/*
              Said in full, because the badge is a promise and the person
              granting it should be reading what it promises at the moment they
              grant it. One meaning: a real artist, and this account is theirs.
            */}
            <p className="max-w-md text-xs leading-relaxed text-faint">
              {user.verified
                ? 'The check shows beside their name everywhere on the platform. Removing it is immediate.'
                : 'Says this is a real artist and this account is genuinely theirs. The check then shows beside their name everywhere: on tracks, in rooms, in chat and in comments.'}
            </p>
          </div>
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Hosting                                                             */}
      {/* ------------------------------------------------------------------ */}
      {/*
        Between account status and the queues, because it is the proportionate
        middle: less than suspending somebody, more than reading a report.
      */}
      <ContentSection user={data} onChange={invalidate} />

      {data.host_applications.length > 0 && (
        <Section title="Host applications">
          <ul className="flex flex-col gap-2">
            {data.host_applications.map((application) => (
              <li
                key={application.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[var(--radius-control)] border border-line p-3 text-sm"
              >
                <Chip tone={application.status === 'approved' ? 'fresh' : 'default'}>
                  {application.status}
                </Chip>
                <span className="text-soft">{shortDate(application.created_at)}</span>
                {application.reviewer && (
                  <span className="text-faint">by @{application.reviewer}</span>
                )}
                {application.decision_note && (
                  <p className="w-full text-xs text-faint">“{application.decision_note}”</p>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Reports                                                             */}
      {/* ------------------------------------------------------------------ */}
      {data.reports && (
        <Section title="Reports">
          <p className="text-sm text-soft">
            {data.reports.about_them} about this account, {data.reports.about_their_content} about
            things they posted.
          </p>

          {data.reports.recent.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {data.reports.recent.map((report) => (
                <li key={report.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <Chip tone={report.status === 'open' ? 'hot' : 'default'}>{report.status}</Chip>
                  <span className="text-ink">{report.reason}</span>
                  <span className="font-mono text-xs text-faint">
                    {report.subject_type} #{report.subject_id}
                  </span>
                  <span className="text-xs text-faint">{shortDate(report.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Notes                                                               */}
      {/* ------------------------------------------------------------------ */}
      {canAward && <AwardCredit userId={user.id} onChange={invalidate} />}

      {canNote && data.notes && <Notes userId={user.id} notes={data.notes} onChange={invalidate} />}

      {/* ------------------------------------------------------------------ */}
      {/* History                                                             */}
      {/* ------------------------------------------------------------------ */}
      {data.history && data.history.length > 0 && (
        <Section title="What has been done">
          <ul className="flex flex-col gap-1.5" data-history>
            {data.history.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-mono text-xs text-accent">{entry.action}</span>
                <span className="text-faint">
                  {entry.actor ? `by @${entry.actor}` : 'from the command line'}
                </span>
                <span className="text-xs text-faint">{shortDate(entry.created_at)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </Frame>
  );
}

/**
 * Granting discovery credit by hand.
 *
 * ## Why the reason is required here and optional everywhere else
 *
 * This is the only control on the panel that creates something rather than
 * restricting it, and "why" is the one part of it a later reader cannot work
 * out for themselves. A suspension explains itself from the account it is on;
 * a hundred points appearing in somebody's ledger does not.
 *
 * The server insists too - this is not a client-side courtesy.
 *
 * ## Points, not boosts
 *
 * The moderator grants the same currency everybody earns and the threshold does
 * the rest, so a grant that crosses it produces a boost through exactly the
 * path a real one does. The response says how many it produced, because that is
 * the only part the moderator cannot work out from the number they typed.
 */
function AwardCredit({ userId, onChange }: { userId: number; onChange: () => void }) {
  const [points, setPoints] = useState(100);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const errors = useFormErrors();

  const award = useMutation({
    mutationFn: () =>
      api.post<{ points: number; available: number; boosts_granted: number }>(
        `/admin/users/${userId}/discovery-credits`,
        { points, note: note.trim() },
      ),
    onError: errors.capture,
    onSuccess: (data) => {
      setNote('');
      setResult(
        data.boosts_granted > 0
          ? `Awarded. That crossed the threshold, so ${data.boosts_granted} ${
              data.boosts_granted === 1 ? 'boost' : 'boosts'
            } granted, ${data.points} points total.`
          : `Awarded. ${data.points} points total.`,
      );
      onChange();
    },
  });

  return (
    <Section title={`Award ${copy['noun.boost.earned.plural'].toLowerCase()}`}>
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-relaxed text-soft">
          Points, not boosts. {THRESHOLD} of them becomes one, the same way an earned boost does.
          This is for putting right what the automatic rules could not.
        </p>

        <div className="flex flex-wrap items-center gap-1.5">
          {[10, 50, 100, 200, 300].map((amount) => (
            <button
              key={amount}
              type="button"
              aria-pressed={points === amount}
              data-award-points={amount}
              onClick={() => setPoints(amount)}
              className={`rounded-[var(--radius-pill)] border px-3.5 py-1.5 font-mono text-[11px] transition-colors ${
                points === amount
                  ? 'border-transparent bg-accent text-accent-ink'
                  : 'border-line text-soft hover:border-line-strong hover:text-ink'
              }`}
            >
              {amount}
              {amount % THRESHOLD === 0 && (
                <span className="ml-1.5 opacity-60">
                  = {amount / THRESHOLD}
                </span>
              )}
            </button>
          ))}
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-soft">Why (required, and recorded)</span>
          <input
            value={note}
            maxLength={200}
            data-award-note
            placeholder="Their room dropped mid-set and ten submissions were lost."
            onChange={(event) => setNote(event.target.value)}
            className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-line-strong"
          />
        </label>

        {errors.fields.points && <Alert tone="error">{errors.fields.points}</Alert>}
        {errors.fields.note && <Alert tone="error">{errors.fields.note}</Alert>}
        {result && <Alert tone="success">{result}</Alert>}

        <div>
          <Button
            loading={award.isPending}
            disabled={note.trim() === ''}
            data-award-submit
            onClick={() => {
              setResult(null);
              errors.clear();
              award.mutate();
            }}
          >
            Award {points} points
          </Button>
        </div>
      </div>
    </Section>
  );
}

/**
 * Staff notes.
 *
 * ## No edit control, on purpose
 *
 * A note that can be rewritten after the fact is not a record of what was known
 * at the time, and the next moderator reading one has no way to tell which they
 * are looking at. Wrong notes get deleted and replaced, which leaves both facts
 * visible.
 *
 * Deleted notes keep their row and lose their body, so "somebody removed a note
 * here" is something the next reader can see.
 */
function Notes({
  userId,
  notes,
  onChange,
}: {
  userId: number;
  notes: NonNullable<AdminUserDetail['notes']>;
  onChange: () => void;
}) {
  const [body, setBody] = useState('');

  const add = useMutation({
    mutationFn: () => api.post(`/admin/users/${userId}/notes`, { body: body.trim() }),
    onSuccess: () => {
      setBody('');
      onChange();
    },
  });

  const remove = useMutation({
    mutationFn: (noteId: number) => api.delete(`/admin/users/${userId}/notes/${noteId}`),
    onSuccess: onChange,
  });

  return (
    <Section title="Staff notes">
      <p className="text-xs text-faint">
        Only staff see these. They cannot be edited (delete and rewrite instead) and they are
        removed with the account.
      </p>

      <div className="mt-3 flex flex-col gap-2">
        <Textarea
          value={body}
          maxLength={2000}
          data-note-body
          onChange={(event) => setBody(event.target.value)}
          placeholder="What happened, and what was decided."
          className="min-h-20 resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
        />
        <div className="flex items-center gap-3">
          <Button
            loading={add.isPending}
            disabled={body.trim() === ''}
            onClick={() => add.mutate()}
            data-add-note
          >
            Add note
          </Button>
          {add.isError && (
            <span className="text-sm text-hot-text">
              {add.error instanceof ApiError ? add.error.message : 'That could not be saved.'}
            </span>
          )}
        </div>
      </div>

      {notes.length > 0 && (
        <ul className="mt-5 flex flex-col gap-3" data-notes>
          {notes.map((note) => (
            <li
              key={note.id}
              data-note={note.id}
              className="rounded-[var(--radius-control)] border border-line p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-xs text-faint">
                  {note.author ? `@${note.author}` : 'account removed'} ·{' '}
                  {fullDate(note.created_at)}
                </span>
                {note.deleted_at === null && (
                  <button
                    type="button"
                    data-delete-note
                    onClick={() => remove.mutate(note.id)}
                    className="text-xs text-faint transition-colors hover:text-hot-text"
                  >
                    Delete
                  </button>
                )}
              </div>

              {note.body === null ? (
                <p className="mt-1.5 text-sm italic text-faint">
                  Note removed{note.deleted_by ? ` by @${note.deleted_by}` : ''}.
                </p>
              ) : (
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-soft">{note.body}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-5">
      <Link to="/admin" className="text-sm text-soft hover:text-ink">
        ← All accounts
      </Link>
      {children}
    </div>
  );
}

/**
 * The account as a form: what the person edits in Settings, plus the handle
 * and the address, which Settings never offers. Same limits as their form;
 * the server enforces them too and its messages land beside the fields.
 */
function AccountForm({ user, onChange }: { user: AdminUserDetail['user']; onChange: () => void }) {
  const [form, setForm] = useState({
    username: user.username,
    display_name: user.display_name,
    email: user.email,
    email_verified: user.email_verified,
    bio: user.bio ?? '',
    location: user.location ?? '',
  });
  const errors = useFormErrors();

  // A refetch after saving brings the canonical values back; the form follows.
  useEffect(() => {
    setForm({
      username: user.username,
      display_name: user.display_name,
      email: user.email,
      email_verified: user.email_verified,
      bio: user.bio ?? '',
      location: user.location ?? '',
    });
  }, [user]);

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((current) => ({
      ...current,
      [key]: key === 'email_verified' ? (event.target as HTMLInputElement).checked : event.target.value,
    }));

  const dirty =
    form.username !== user.username ||
    form.display_name !== user.display_name ||
    form.email !== user.email ||
    form.email_verified !== user.email_verified ||
    form.bio !== (user.bio ?? '') ||
    form.location !== (user.location ?? '');

  const save = useMutation({
    mutationFn: () => {
      // Only what changed, so the audit row says what was done and the
      // address is not un-verified by a save that never touched it.
      const patch: Record<string, unknown> = {};

      if (form.username !== user.username) patch.username = form.username;
      if (form.display_name !== user.display_name) patch.display_name = form.display_name;
      if (form.email !== user.email) patch.email = form.email;
      if (form.email_verified !== user.email_verified) patch.email_verified = form.email_verified;
      if (form.bio !== (user.bio ?? '')) patch.bio = form.bio;
      if (form.location !== (user.location ?? '')) patch.location = form.location;

      return api.patch(`/admin/users/${user.id}`, patch);
    },
    onSuccess: () => {
      errors.clear();
      onChange();
    },
    onError: errors.capture,
  });

  return (
    <Section title="Account details">
      <form
        className="grid gap-4 sm:grid-cols-2"
        data-account-form
        onSubmit={(event) => {
          event.preventDefault();
          if (dirty) save.mutate();
        }}
      >
        <Field
          label="Display name"
          value={form.display_name}
          maxLength={60}
          onChange={set('display_name')}
          error={errors.fields.display_name}
          data-field="display_name"
        />
        <Field
          label="Username"
          value={form.username}
          maxLength={30}
          onChange={set('username')}
          error={errors.fields.username}
          hint="Letters, numbers, underscores and dashes. Their address on the site changes with it."
          data-field="username"
        />
        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={set('email')}
          error={errors.fields.email}
          hint="Changing it marks the new address unverified unless the box below is ticked."
          data-field="email"
        />
        <div className="flex flex-col justify-end gap-1.5 pb-1">
          <Checkbox
            label="Email verified"
            hint="Tick to vouch for the address without them clicking a link."
            checked={form.email_verified}
            onChange={set('email_verified')}
            data-field="email_verified"
          />
        </div>
        <Field
          label="Location"
          value={form.location}
          maxLength={80}
          onChange={set('location')}
          error={errors.fields.location}
          data-field="location"
        />
        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-sm font-medium text-ink">Bio</span>
          <Textarea
            value={form.bio}
            maxLength={500}
            onChange={set('bio')}
            invalid={Boolean(errors.fields.bio)}
            className="min-h-24"
            data-field="bio"
          />
          {errors.fields.bio && <span className="text-xs text-hot-text">{errors.fields.bio}</span>}
        </label>

        <div className="flex items-center gap-3 sm:col-span-2">
          <Button type="submit" loading={save.isPending} disabled={!dirty} data-save-account>
            Save changes
          </Button>
          {errors.message && <span className="text-sm text-hot-text">{errors.message}</span>}
          {save.isSuccess && !dirty && <span className="text-sm text-faint">Saved.</span>}
        </div>
      </form>
    </Section>
  );
}

/**
 * The support actions: what staff do for somebody locked out or taken over,
 * and the way to close the account. Nothing here shows staff a secret; the
 * reset link goes to the address on file.
 */
function AccessSection({
  user,
  isSelf,
  canEdit,
  canDelete,
}: {
  user: AdminUserDetail['user'];
  isSelf: boolean;
  canEdit: boolean;
  canDelete: boolean;
}) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const act = useMutation({
    mutationFn: (what: 'password-reset' | 'verification' | 'sessions') =>
      api.post(`/admin/users/${user.id}/${what}`),
    onSuccess: (_result, what) =>
      setNote(
        what === 'sessions'
          ? 'Signed out on every device. Their next request asks them to sign in.'
          : what === 'verification'
            ? `Verification link sent to ${user.email}.`
            : `Reset link sent to ${user.email}. It expires the way theirs would.`,
      ),
    onError: (error) => setNote(error instanceof ApiError ? error.message : 'That did not go through.'),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/admin/users/${user.id}`, { body: { confirm } }),
    onSuccess: () => navigate('/admin/users'),
  });

  const ready = confirm.trim().toLowerCase() === user.username.toLowerCase();

  return (
    <Section title="Access">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2" data-access-actions>
          <Button variant="secondary" loading={act.isPending && act.variables === 'password-reset'} onClick={() => act.mutate('password-reset')} data-send-reset>
            Send a password reset
          </Button>
          {!user.email_verified && (
            <Button variant="secondary" loading={act.isPending && act.variables === 'verification'} onClick={() => act.mutate('verification')} data-send-verification>
              Resend verification
            </Button>
          )}
          <Button variant="ghost" disabled={isSelf} loading={act.isPending && act.variables === 'sessions'} onClick={() => act.mutate('sessions')} data-revoke-sessions>
            Sign out everywhere
          </Button>
        </div>
      )}
      {note && <p className="mt-2 text-sm text-soft">{note}</p>}

      {canDelete && (
        <div className="mt-5 border-t border-line pt-5" data-delete-account>
          <p className="text-sm text-ink">Delete this account</p>
          <p className="mt-1 max-w-lg text-xs leading-relaxed text-faint">
            Removes their tracks, playlists, pictures and profile for good, frees the email address,
            retires the username and signs them out. Their messages in other people&apos;s threads stay.
            The same thing they get from Settings; there is no undo.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <TextInput
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              placeholder={`Type ${user.username} to confirm`}
              disabled={isSelf}
              className="w-64"
              data-delete-confirm
            />
            <Button
              variant="danger"
              disabled={!ready || isSelf}
              loading={remove.isPending}
              onClick={() => remove.mutate()}
              data-delete-user
            >
              Delete account
            </Button>
          </div>
          {isSelf && <p className="mt-2 text-xs text-faint">Delete your own account from Settings.</p>}
          {remove.isError && (
            <p className="mt-2 text-sm text-hot-text">
              {remove.error instanceof ApiError ? remove.error.message : 'That did not go through.'}
            </p>
          )}
        </div>
      )}
    </Section>
  );
}

/** How long a mute lasts, as the menu offers it. Minutes; null is "until lifted". */
const MUTE_LENGTHS: { label: string; minutes: number | null }[] = [
  { label: '1 hour', minutes: 60 },
  { label: '24 hours', minutes: 60 * 24 },
  { label: '3 days', minutes: 60 * 24 * 3 },
  { label: '7 days', minutes: 60 * 24 * 7 },
  { label: '30 days', minutes: 60 * 24 * 30 },
  { label: 'Until lifted', minutes: null },
];

/**
 * Taking somebody's voice for a while, and giving it back.
 *
 * The step between a note and a suspension: they keep the account, the
 * music and the audience, and stop being able to comment, message, chat,
 * post or review. The reason is required and goes to them word for word.
 */
function MuteSection({
  user,
  mute,
  history,
  isSelf,
  onChange,
}: {
  user: AdminUserDetail['user'];
  mute: AdminUserDetail['mute'];
  history: AdminUserDetail['mutes'];
  isSelf: boolean;
  onChange: () => void;
}) {
  const [reason, setReason] = useState('');
  const [length, setLength] = useState(1);
  const errors = useFormErrors();

  const apply = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${user.id}/mute`, { reason: reason.trim(), minutes: MUTE_LENGTHS[length]?.minutes ?? null }),
    onSuccess: () => {
      setReason('');
      errors.clear();
      onChange();
    },
    onError: errors.capture,
  });

  const lift = useMutation({
    mutationFn: () => api.delete(`/admin/users/${user.id}/mute`),
    onSuccess: onChange,
  });

  return (
    <Section title="Mute">
      {mute ? (
        <div className="flex flex-col gap-3" data-mute-active>
          <p className="text-sm text-ink">
            Muted {mute.expires_at ? `until ${fullDate(mute.expires_at)}` : 'until lifted'}
            {mute.by ? ` by @${mute.by}` : ''}, since {fullDate(mute.created_at)}.
          </p>
          <p className="rounded-[var(--radius-control)] border border-line bg-app p-3 text-sm text-soft">
            “{mute.reason}”
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" loading={lift.isPending} onClick={() => lift.mutate()} data-unmute>
              Lift the mute
            </Button>
            <p className="text-xs text-faint">They are told the moment it is lifted.</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-mute-form>
          <p className="max-w-lg text-xs leading-relaxed text-faint">
            They keep the account, their music and their followers, and stop being able to comment,
            message, chat in rooms, post updates, review or repost with a caption. They see the reason
            you write here, word for word, and are told when it ends.
          </p>
          <Textarea
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why, written to them. For example: Slurs in a room's chat are not allowed here."
            invalid={Boolean(errors.fields.reason)}
            className="min-h-20"
            data-mute-reason
          />
          {errors.fields.reason && <span className="text-xs text-hot-text">{errors.fields.reason}</span>}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={length}
              onChange={(event) => setLength(Number(event.target.value))}
              className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink"
              aria-label="How long"
              data-mute-length
            >
              {MUTE_LENGTHS.map((option, index) => (
                <option key={option.label} value={index}>
                  {option.label}
                </option>
              ))}
            </select>
            <Button
              variant="danger"
              disabled={isSelf || reason.trim() === ''}
              loading={apply.isPending}
              onClick={() => apply.mutate()}
              data-mute
            >
              Mute account
            </Button>
            {errors.message && <span className="text-sm text-hot-text">{errors.message}</span>}
          </div>
          {isSelf && <p className="text-xs text-faint">You cannot mute your own account.</p>}
        </div>
      )}

      {history.length > 0 && (
        <ul className="mt-4 flex flex-col gap-1.5 border-t border-line pt-4" data-mute-history>
          {history.map((row) => (
            <li key={row.id} className="flex flex-wrap items-baseline gap-x-2 text-xs text-faint">
              <span className="text-soft">{shortDate(row.created_at)}</span>
              {row.muted_by && <span>by @{row.muted_by}</span>}
              <span>{row.expires_at ? `until ${shortDate(row.expires_at)}` : 'open-ended'}</span>
              {row.lifted_at && <span>lifted {shortDate(row.lifted_at)}{row.lifted_by ? ` by @${row.lifted_by}` : ''}</span>}
              <span className="w-full text-soft">“{row.reason}”</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="meta-caps mb-3 text-faint">{title}</h2>
      <Card className="p-5">{children}</Card>
    </section>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="meta-caps text-faint">{label}</span>
      <span className={`truncate text-sm text-ink ${mono ? 'font-mono text-xs' : ''}`} title={value}>
        {value}
      </span>
    </div>
  );
}

function Stat({ label, value, tone = false }: { label: string; value: number; tone?: boolean }) {
  return (
    <Card className="flex flex-col gap-1 p-3">
      <span className="meta-caps text-faint">{label}</span>
      <span className={`font-mono text-lg ${tone ? 'text-hot-text' : 'text-ink'}`}>
        {value.toLocaleString()}
      </span>
    </Card>
  );
}

function shortDate(iso: string): string {
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function fullDate(iso: string): string {
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * What this account has uploaded, and the buttons that take it down.
 *
 * ## Two sizes of action, on purpose
 *
 * Removing a picture is not removing a track, and neither is suspending an
 * account. The cases that actually arrive are small - a good track with an
 * obscene cover, an account whose only problem is its banner - and a panel
 * offering only the largest hammer is how a moderator ends up using it.
 *
 * `media.remove` and `track.delete.any` are separate permissions for the same
 * reason, so the buttons appear separately too.
 *
 * ## Everything here notifies the owner
 *
 * The server does it, not this. Worth knowing while clicking: the artist finds
 * out from us rather than from a hole in their profile, which is what makes an
 * appeal possible. Every action also writes to the audit log.
 */
function ContentSection({
  user,
  onChange,
}: {
  user: AdminUserDetail;
  onChange: () => void;
}) {
  const canRemoveMedia = useCan('media.remove');
  const canDeleteTracks = useCan('track.delete.any');
  const canSetVisibility = useCan('track.edit.any');

  /*
   * The page carries the first page of uploads; anything after it is
   * asked for here and kept beside it. Held locally rather than pushed
   * into the account's query, because the rest of that document is not
   * re-read when somebody presses "show more".
   */
  const [extra, setExtra] = useState<AdminUserDetail['tracks']>([]);
  const [more, setMore] = useState<string | null>(user.tracks_cursor);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setExtra([]);
    setMore(user.tracks_cursor);
  }, [user.user.id, user.tracks_cursor]);

  const tracks = [...user.tracks, ...extra];

  async function loadMore() {
    if (more === null || loadingMore) return;

    setLoadingMore(true);

    try {
      const next = await api.get<{ items: AdminUserDetail['tracks']; next_cursor: string | null }>(
        `/admin/users/${user.user.id}/tracks?cursor=${more}`,
      );

      setExtra((held) => [...held, ...next.items]);
      setMore(next.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  }

  if (!canRemoveMedia && !canDeleteTracks && !canSetVisibility) return null;

  return (
    <Section title="Uploaded content">
      <div className="flex flex-col gap-5">
        {canRemoveMedia && (
          <div className="flex flex-wrap gap-4">
            <Picture
              label="Avatar"
              url={user.user.avatar}
              kind="avatar"
              id={user.user.id}
              onChange={onChange}
            />
            <Picture
              label="Header"
              url={user.user.banner}
              kind="banner"
              id={user.user.id}
              onChange={onChange}
              wide
            />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <span className="meta-caps text-faint">tracks</span>
            <span className="font-mono text-[10px] text-ghost">
              {tracks.length}
              {more !== null && ' so far'}
            </span>
          </div>

          {tracks.length === 0 ? (
            <p className="text-sm text-soft">Nothing uploaded.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {tracks.map((track) => (
                <li
                  key={track.id}
                  className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line p-2.5"
                >
                  {track.artwork ? (
                    <img
                      src={track.artwork}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)] object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)] bg-raised" />
                  )}

                  <div className="flex min-w-0 flex-1 flex-col">
                    <DrawerLink
                      to={`/tracks/${track.id}`}
                      className="truncate text-sm font-medium text-ink hover:underline"
                    >
                      {track.title}
                    </DrawerLink>
                    <span className="font-mono text-[10px] text-faint">
                      {track.status}
                      {!canSetVisibility && track.visibility !== 'public' && ` · ${track.visibility}`}
                      {track.held && ' · on hold'}
                    </span>
                  </div>

                  {/*
                    Who can see it, changed here rather than by asking the
                    artist to do it: a track pulled out of sight while a
                    report is looked at, or put back afterwards. Both
                    directions are written to the audit log and the artist
                    is told - see `SetTrackVisibilityAction`.
                  */}
                  {canSetVisibility && (
                    <Visibility track={track} onChange={onChange} />
                  )}

                  {canRemoveMedia && track.artwork && (
                    <Danger
                      label="remove art"
                      confirm="remove art?"
                      onConfirm={() => api.delete(`/admin/media/track-artwork/${track.id}`)}
                      onDone={onChange}
                    />
                  )}

                  {canDeleteTracks && (
                    <Danger
                      label="delete"
                      confirm="delete for good?"
                      onConfirm={() => api.delete(`/tracks/${track.id}`)}
                      onDone={onChange}
                    />
                  )}
                </li>
              ))}

              {/*
                The rest of them. A list that stops at fifty was fine while
                it was for spotting the track a report named; it is not
                fine now that every row has a control on it.
              */}
              {more !== null && (
                <li className="flex justify-center pt-1">
                  <Button
                    variant="ghost"
                    loading={loadingMore}
                    data-admin-tracks-more
                    onClick={() => void loadMore()}
                  >
                    Show more
                  </Button>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </Section>
  );
}

/**
 * Who can see one track, as a menu rather than three buttons.
 *
 * The change is announced in place rather than by a toast: the person who
 * pressed it is looking at the row, and a page-corner message about a
 * track they cannot see from there is a message about nothing.
 */
function Visibility({
  track,
  onChange,
}: {
  track: AdminUserDetail['tracks'][number];
  onChange: () => void;
}) {
  const [value, setValue] = useState(track.visibility);
  const [problem, setProblem] = useState<string | null>(null);

  const set = useMutation({
    mutationFn: (next: string) =>
      api.patch<{ visibility: string }>(`/admin/tracks/${track.id}/visibility`, { visibility: next }),
    onMutate: (next: string) => {
      setValue(next);
      setProblem(null);
    },
    onSuccess: () => onChange(),
    onError: (error) => {
      // Back to what the server still says it is.
      setValue(track.visibility);
      setProblem(error instanceof ApiError ? error.message : 'That did not go through.');
    },
  });

  return (
    <span className="flex shrink-0 flex-col items-end gap-1">
      <Select
        value={value}
        options={[
          { value: 'public', label: 'public' },
          { value: 'unlisted', label: 'unlisted' },
          { value: 'private', label: 'private' },
        ]}
        onChange={(next: string) => set.mutate(next)}
        disabled={set.isPending}
        aria-label={`Who can see ${track.title}`}
        data-track-visibility={track.id}
        className="w-[7.5rem]"
      />
      {problem !== null && <span className="meta text-negative">{problem}</span>}
    </span>
  );
}

/** One removable image, shown at a size somebody can actually judge. */
function Picture({
  label,
  url,
  kind,
  id,
  onChange,
  wide = false,
}: {
  label: string;
  url: string | null;
  kind: string;
  id: number;
  onChange: () => void;
  wide?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="meta-caps text-faint">{label}</span>

      {url ? (
        <>
          <img
            src={url}
            alt=""
            className={`rounded-[var(--radius-control)] border border-line object-cover ${
              wide ? 'h-20 w-[220px]' : 'h-20 w-20'
            }`}
          />
          <Danger
            label="remove"
            confirm="remove it?"
            onConfirm={() => api.delete(`/admin/media/${kind}/${id}`)}
            onDone={onChange}
          />
        </>
      ) : (
        <div
          className={`flex items-center justify-center rounded-[var(--radius-control)] border border-dashed border-line text-[10px] text-ghost ${
            wide ? 'h-20 w-[220px]' : 'h-20 w-20'
          }`}
        >
          none
        </div>
      )}
    </div>
  );
}

/**
 * A destructive button that arms rather than opening a dialog.
 *
 * A modal would cover the row containing the thing being removed, so you would
 * confirm the deletion of something you could no longer see. Arming in place
 * keeps the title, the artwork and the button together.
 *
 * Disarms on a timer, so one left armed does not wait for the next person.
 */
function Danger({
  label,
  confirm,
  onConfirm,
  onDone,
}: {
  label: string;
  confirm: string;
  onConfirm: () => Promise<unknown>;
  onDone: () => void;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);

    return () => clearTimeout(timer);
  }, [armed]);

  const run = useMutation({
    mutationFn: onConfirm,
    onSuccess: () => {
      setArmed(false);
      onDone();
    },
  });

  return (
    <button
      type="button"
      data-danger={label}
      disabled={run.isPending}
      onClick={() => (armed ? run.mutate() : setArmed(true))}
      className={`shrink-0 rounded-[var(--radius-chip)] border px-2.5 py-1.5 font-mono text-[10px] transition-colors ${
        armed
          ? 'border-hot-edge bg-hot-wash text-hot-text'
          : 'border-line text-soft hover:border-line-strong hover:text-ink'
      }`}
    >
      {run.isPending ? 'working…' : armed ? confirm : label}
    </button>
  );
}
